<template>
  <div class="MenuItemGpts">
    <UIAccordion class="MenuItemGpts__accordion" :isOpen="false">
      <template #toggle-container="{ isOpen, toggle }">
        <RouterLink
          v-slot="{ isActive: isRouteActive, href, navigate }"
          custom
          :to="{ name: 'GptList' }"
        >
          <a :href="href" class="MenuItemGpts__toggleLink" @click="navigate">
            <TheSidebarMenuItem
              class="MenuItemGpts__item MenuItemGpts__item--toggle"
              :isActive="isRouteActive"
            >
              <template #icon>
                <UIIconLocal name="gpts" />
              </template>
              <template #label>GPTs</template>
              <template #contextMenu>
                <button
                  type="button"
                  class="MenuItemGpts__toggle-icon-button"
                  :aria-expanded="isOpen"
                  aria-controls="pinned-gpts-list"
                  aria-label="Toggle pinned GPTs"
                  @click.stop.prevent="toggle"
                >
                  <UIIcon
                    name="chevronDown"
                    class="MenuItemGpts__toggle-icon"
                    :class="{ 'MenuItemGpts__toggle-icon--collapsed': !isOpen }"
                  />
                </button>
              </template>
            </TheSidebarMenuItem>
          </a>
        </RouterLink>
      </template>

      <template #content>
        <div id="pinned-gpts-list" class="MenuItemGpts__pinnedList">
          <div
            v-for="gpt in displayPinnedGpts"
            :key="gpt.id"
            class="MenuItemGpts__pinnedRow"
            :data-drop-target="isDropTarget(gpt.id)"
            :data-drag-source="isDragSource(gpt.id)"
            @dragover="onDragOver($event, gpt.id)"
            @drop="onDrop($event)"
          >
            <RouterLink
              :to="{ name: 'NewChat', query: { gptUuid: gpt.id } }"
              :draggable="true"
              class="MenuItemGpts__link MenuItemGpts__link--pinnedDraggable"
              @dragstart="onDragStart($event, gpt.id)"
              @dragend="onDragEnd"
              @click="onRowClick"
            >
              <TheSidebarMenuItem
                class="MenuItemGpts__item"
                :isActive="isActive(gpt.id)"
              >
                <template #icon>
                  <div class="MenuItemGpts__iconWrap">
                    <span class="MenuItemGpts__dragHandle" aria-hidden="true">
                      <UIIcon
                        name="gripDotsVerticalRegular"
                        class="MenuItemGpts__dragIcon"
                      />
                    </span>
                    <UIGradientSphere
                      :gradient="gpt.gradient"
                      size="sm"
                      class="MenuItemGpts__sphere"
                    />
                  </div>
                </template>
                <template #label>
                  <div class="MenuItemGpts__label" :title="gpt.name">
                    {{ gpt.name }}
                  </div>
                </template>

                <template #contextMenu>
                  <GptContextMenu
                    class="MenuItemGpts__context-menu"
                    :gpt="gpt"
                  />
                </template>
              </TheSidebarMenuItem>
            </RouterLink>
          </div>
        </div>
      </template>
    </UIAccordion>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { UIIcon } from '@foundever/ui-lib'
import { useRoute } from 'vue-router'

import { PINNED_GPTS_QUERY_FILTERS } from '@/apiClient/queries/gptCacheUtils'
import { useGptQueries } from '@/apiClient/queries/useGptQueries'
import UIAccordion from '@/views/components/ui/accordion/UIAccordion.vue'
// `gpts` is bespoke EverGPT artwork with no ui-lib counterpart, so that one glyph
// still comes from the local icon set; everything else here is ui-lib's.
import UIIconLocal from '@/views/components/ui/icons/UIIcon.vue'
import UIGradientSphere from '@/views/components/ui/misc/UIGradientSphere.vue'
import GptContextMenu from '@/views/pages/Gpt/List/components/GptContextMenu.vue'

import { useDragReorderList } from '../../composables/useDragReorderList'
import TheSidebarMenuItem from '../TheSidebarMenuItem.vue'

const PINNED_FILTERS = PINNED_GPTS_QUERY_FILTERS

const { list, setPinOrder } = useGptQueries()
const { data: pinnedData } = list(PINNED_FILTERS)

const route = useRoute()

const pinnedGpts = computed(() => pinnedData.value?.items ?? [])

const {
  displayItems: displayPinnedGpts,
  isDropTarget,
  isDragSource,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onRowClick,
} = useDragReorderList({
  items: pinnedGpts,
  rowSelector: '.MenuItemGpts__pinnedRow',
  onCommit: (gptIds) => setPinOrder.mutateAsync({ gpt_ids: gptIds }),
})

const isActive = (gptId: string) => {
  return route.name === 'NewChat' && route.query.gptUuid === gptId
}
</script>

<style scoped lang="scss">
.MenuItemGpts {
  .MenuItemGpts__toggleLink {
    display: block;
    width: 100%;
    text-decoration: none;
    color: var(--text-primary);
  }

  :deep(.TheSidebarMenuItem--active.MenuItemGpts__item--toggle) {
    .MenuItemGpts__toggle-icon {
      --color: var(--icon-accent);
    }
  }

  .MenuItemGpts__toggle-icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    padding: 0.25rem;
    margin: -0.25rem;
    background: transparent;
    border-radius: var(--radius-xs);
    cursor: pointer;

    &:hover {
      background: var(--interactive-bg-tertiary-hover, #f2f2f3);
    }
  }

  .MenuItemGpts__link {
    text-decoration: none;
    color: var(--text-primary);

    &--pinnedDraggable {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }
  }

  .MenuItemGpts__label {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .MenuItemGpts__toggle-icon {
    --size: 0.75rem;
    --color: var(--icon-tertiary);
    display: block;
    transition: transform 0.15s ease-in-out;

    &--collapsed {
      transform: rotate(-90deg);
    }
  }

  .MenuItemGpts__pinnedList {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .MenuItemGpts__pinnedRow {
    border-radius: var(--radius-xs);
    transition: background-color 0.15s ease-in-out;

    &[data-drop-target='true'] {
      background-color: var(--interactive-bg-tertiary-hover);
    }

    &[data-drag-source='true'] :deep(.TheSidebarMenuItem) {
      opacity: 0.35;
      transition: opacity 0.15s ease-in-out;
    }
  }

  .MenuItemGpts__iconWrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  .MenuItemGpts__dragHandle {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    z-index: 1;
  }

  .MenuItemGpts__sphere {
    transition: opacity 0.2s ease-in-out;
  }

  .MenuItemGpts__dragIcon {
    --size: 1rem !important;
    width: 1rem;
    height: 1rem;
    color: var(--icon-primary);
  }

  .MenuItemGpts__pinnedRow:hover .MenuItemGpts__dragHandle {
    opacity: 1;
  }

  .MenuItemGpts__pinnedRow:hover .MenuItemGpts__sphere {
    opacity: 0;
  }

  .MenuItemGpts__pinnedRow[data-drag-source='true'] .MenuItemGpts__dragHandle,
  .MenuItemGpts__pinnedRow[data-drop-target='true'] .MenuItemGpts__dragHandle {
    opacity: 1;
  }

  .MenuItemGpts__pinnedRow[data-drag-source='true'] .MenuItemGpts__sphere,
  .MenuItemGpts__pinnedRow[data-drop-target='true'] .MenuItemGpts__sphere {
    opacity: 0;
  }
}
</style>

<style lang="scss">
.MenuItemGpts__context-menu {
  --gpt-context-menu-icon-size: 1rem;
}

.MenuItemGpts__item {
  &:hover {
    .GptContextMenu__menu {
      display: inline-flex;
    }
  }
}
</style>
